/**
 * Responsive Utilities for Smart Grid
 * Provides utilities for handling responsive behavior, breakpoints, and viewport detection
 */

/**
 * Breakpoint type definition
 */
export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

/**
 * Breakpoint configuration interface
 */
export interface BreakpointConfig {
  mobile: number;    // Max width for mobile (default: 640px)
  tablet: number;    // Max width for tablet (default: 1024px)
}

/**
 * Default breakpoint values (Tailwind CSS breakpoints)
 */
export const DEFAULT_BREAKPOINTS: BreakpointConfig = {
  mobile: 640,
  tablet: 1024,
};

/**
 * Detects the current breakpoint based on viewport width
 * @param width - Current viewport width
 * @param breakpoints - Custom breakpoint configuration (optional)
 * @returns Current breakpoint ('mobile' | 'tablet' | 'desktop')
 */
export function getCurrentBreakpoint(
  width: number,
  breakpoints: BreakpointConfig = DEFAULT_BREAKPOINTS
): Breakpoint {
  if (width <= breakpoints.mobile) {
    return 'mobile';
  } else if (width <= breakpoints.tablet) {
    return 'tablet';
  }
  return 'desktop';
}

/**
 * Checks if the current breakpoint is mobile
 * @param width - Current viewport width
 * @param breakpoints - Custom breakpoint configuration (optional)
 * @returns True if mobile breakpoint
 */
export function isMobile(
  width: number,
  breakpoints: BreakpointConfig = DEFAULT_BREAKPOINTS
): boolean {
  return getCurrentBreakpoint(width, breakpoints) === 'mobile';
}

/**
 * Checks if the current breakpoint is tablet
 * @param width - Current viewport width
 * @param breakpoints - Custom breakpoint configuration (optional)
 * @returns True if tablet breakpoint
 */
export function isTablet(
  width: number,
  breakpoints: BreakpointConfig = DEFAULT_BREAKPOINTS
): boolean {
  return getCurrentBreakpoint(width, breakpoints) === 'tablet';
}

/**
 * Checks if the current breakpoint is desktop
 * @param width - Current viewport width
 * @param breakpoints - Custom breakpoint configuration (optional)
 * @returns True if desktop breakpoint
 */
export function isDesktop(
  width: number,
  breakpoints: BreakpointConfig = DEFAULT_BREAKPOINTS
): boolean {
  return getCurrentBreakpoint(width, breakpoints) === 'desktop';
}

/**
 * Filters columns based on current breakpoint
 * Removes columns that should be hidden for the current viewport
 * @param columns - Array of column definitions
 * @param breakpoint - Current breakpoint
 * @param hideOnMobile - Array of field names to hide on mobile
 * @param hideOnTablet - Array of field names to hide on tablet
 * @returns Filtered array of columns
 */
export function getResponsiveColumns<T>(
  columns: any[],
  breakpoint: Breakpoint,
  hideOnMobile: (keyof T)[] = [],
  hideOnTablet: (keyof T)[] = []
): any[] {
  if (breakpoint === 'mobile') {
    return columns.filter(col => !hideOnMobile.includes(col.field));
  } else if (breakpoint === 'tablet') {
    return columns.filter(col => !hideOnTablet.includes(col.field));
  }
  return columns;
}

/**
 * Gets the appropriate view mode based on configuration and breakpoint
 * @param breakpoint - Current breakpoint
 * @param mobileView - Preferred mobile view ('table' | 'cards')
 * @returns View mode to use
 */
export function getViewMode(
  breakpoint: Breakpoint,
  mobileView: 'table' | 'cards' = 'table'
): 'table' | 'cards' {
  if (breakpoint === 'mobile') {
    return mobileView;
  }
  return 'table';
}

/**
 * Determines if touch-optimized controls should be used
 * @param breakpoint - Current breakpoint
 * @returns True if touch controls should be used
 */
export function shouldUseTouchControls(breakpoint: Breakpoint): boolean {
  return breakpoint === 'mobile' || breakpoint === 'tablet';
}

/**
 * Gets the optimal page size for the current breakpoint
 * Smaller viewports benefit from smaller page sizes
 * @param breakpoint - Current breakpoint
 * @param defaultPageSize - Default page size
 * @returns Optimal page size
 */
export function getOptimalPageSize(
  breakpoint: Breakpoint,
  defaultPageSize: number = 50
): number {
  switch (breakpoint) {
    case 'mobile':
      return Math.min(defaultPageSize, 20);
    case 'tablet':
      return Math.min(defaultPageSize, 30);
    default:
      return defaultPageSize;
  }
}

/**
 * Gets the optimal item size for virtual scrolling based on breakpoint
 * @param breakpoint - Current breakpoint
 * @param defaultSize - Default item size
 * @returns Optimal item size in pixels
 */
export function getOptimalItemSize(
  breakpoint: Breakpoint,
  defaultSize: number = 48
): number {
  switch (breakpoint) {
    case 'mobile':
      return Math.max(defaultSize, 56); // Larger touch targets
    case 'tablet':
      return Math.max(defaultSize, 52);
    default:
      return defaultSize;
  }
}

/**
 * Determines if compact mode should be enabled
 * @param breakpoint - Current breakpoint
 * @param compactModeOnTablet - Whether to enable compact mode on tablet
 * @returns True if compact mode should be used
 */
export function shouldUseCompactMode(
  breakpoint: Breakpoint,
  compactModeOnTablet: boolean = true
): boolean {
  return breakpoint === 'tablet' && compactModeOnTablet;
}

/**
 * Gets CSS classes for responsive grid container
 * @param breakpoint - Current breakpoint
 * @param compactMode - Whether compact mode is enabled
 * @returns String of CSS classes
 */
export function getResponsiveClasses(
  breakpoint: Breakpoint,
  compactMode: boolean = false
): string {
  const classes = [`breakpoint-${breakpoint}`];

  if (compactMode) {
    classes.push('compact-mode');
  }

  if (breakpoint === 'mobile') {
    classes.push('mobile-view', 'touch-optimized');
  } else if (breakpoint === 'tablet') {
    classes.push('tablet-view', 'touch-optimized');
  } else {
    classes.push('desktop-view');
  }

  return classes.join(' ');
}

/**
 * Gets the minimum touch target size (44x44px recommended)
 * @returns Minimum touch target size in pixels
 */
export function getMinTouchTargetSize(): number {
  return 44; // Apple Human Interface Guidelines recommendation
}

/**
 * Determines if a column is critical and should always be visible
 * @param field - Column field name
 * @param criticalFields - Array of fields that are always visible
 * @returns True if column is critical
 */
export function isCriticalColumn<T>(
  field: keyof T,
  criticalFields: (keyof T)[] = []
): boolean {
  return criticalFields.includes(field);
}

/**
 * Gets optimal column count for current breakpoint
 * Useful for card grid layouts
 * @param breakpoint - Current breakpoint
 * @returns Number of columns
 */
export function getOptimalColumnCount(breakpoint: Breakpoint): number {
  switch (breakpoint) {
    case 'mobile':
      return 1;
    case 'tablet':
      return 2;
    default:
      return 3;
  }
}

/**
 * Formats viewport width for display/debugging
 * @param width - Viewport width in pixels
 * @param breakpoints - Breakpoint configuration
 * @returns Formatted string
 */
export function formatViewportInfo(
  width: number,
  breakpoints: BreakpointConfig = DEFAULT_BREAKPOINTS
): string {
  const breakpoint = getCurrentBreakpoint(width, breakpoints);
  return `${width}px (${breakpoint})`;
}
